import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  ReactiveFormsModule,
  FormGroup,
  Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { debounceTime, filter } from 'rxjs/operators';

import { ScreenSizeService } from '../../services/screen-size/screen-size.service';

import { CustomButtonComponent } from '../custom-button/custom-button.component';
import { Subscription } from 'rxjs';
import { ValidationService } from '../../services/validation/validation.service';


@Component({
  selector: 'app-custom-input',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    CommonModule,
    MatButtonModule,
    CustomButtonComponent,
  ],
  templateUrl: './custom-input.component.html',
  styleUrls: ['./custom-input.component.scss'],
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
})
export class CustomInputComponent implements OnInit {
  @Input() elementId: string | null = null;
  @Input() actionElementId: string | null = null;
  @Input() resetElementId: string | null = null;
  @Input() required: boolean = false; // added this to handle issue on asterisk not reflecting, explicitly declare value for required
  @Input() label: string | null = null;
  @Input() placeholder: string | null = null;
  @Input() controlName: string | null = null;
  @Input() icon: string | null = null;
  @Input() iconPlacement: 'left' | 'right' = 'left';
  @Input() density: number = 0;
  @ContentChild('errorsTemplate', { static: false })
  errorsTemplateRef!: TemplateRef<any>;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() type: string = 'default';
  @Input() collapsible: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() minLength: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | null = null;
  @Input() alphanumeric: boolean = false;
  @Input() allowHyphen: boolean = false;
  @Input() actionIcon: string | null = null;
  @Input() actionText: string | null = null;
  @Input() hideSubscript: boolean = false;
  @Output() onKeypress: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onActionClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() onBlur: EventEmitter<any> = new EventEmitter<any>();
  @Input() resettable: boolean = false;

  //Textarea inputs
  @Input() rows: number = 4;

  screenSize?: string;
  formGroup!: FormGroup;
  isExpanded = false;
  inputSubscription: Subscription = new Subscription();

  constructor(
    private ctrlContainer: FormGroupDirective,
    private screenSizeService: ScreenSizeService,
    private validationService: ValidationService
  ) {}

  ngOnInit(): void {
    this.screenSizeService.screenSize$.subscribe((size) => {
      this.screenSize = size;
    });

    this.formGroup = this.ctrlContainer.form;

    if (!this.controlName) {
      throw new Error('Control name is required');
    }

    if (!this.formGroup.get(this.controlName)) {
      throw new Error(`Control ${this.controlName} not found in parent form`);
    }

    if (this.disabled) {
      this.formGroup.get(this.controlName)?.disable({emitEvent:false});
    }

    this.setFormControlValidators();

    const control = this.formGroup.get(this.controlName);

    if (control?.hasValidator && control.hasValidator(Validators.required)) {
      this.required = true;
    }
    
    if (this.alphanumeric) {
      const allowedCharsPattern = this.allowHyphen ? /[^a-zA-Z0-9-]/g : /[^a-zA-Z0-9]/g;
      this.inputSubscription = control?.valueChanges.pipe(
        filter(value => !!value && typeof value === 'string'),
      ).subscribe(value => {
        const processedValue = value.replace(allowedCharsPattern, '')

        if (value !== processedValue) {
          control.setValue(processedValue, { emitEvent: false });
        }
      }) as Subscription;
    } else {
      this.inputSubscription = control?.valueChanges.pipe(
        debounceTime(700),
        filter(value => !!value && typeof value === 'string'),
      ).subscribe(value => {
        const processedValue = this.trimSpaces(value);
          
        if (value !== processedValue) {
          control.setValue(processedValue, { emitEvent: false });
        }
      }) as Subscription;
    }
  }

  ngOnDestroy() {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe()
    }
  }

  setFormControlValidators(): void {
    // If search input, will not add path traversal validator
    if (!this.controlName || (this.icon == 'search' && this.resettable)) return;

    const control = this.formGroup.get(this.controlName);
    if (!control) return;

    control.addValidators(this.validationService.pathTraversalValidator());
    control.updateValueAndValidity({ emitEvent: false });
  }

  emitKeypressEvent(event: KeyboardEvent) {
    this.onKeypress.emit(event);
  }

  emitBlurEvent(event: any) {
    this.onBlur.emit(event);
  }

  onActionButtonClick() {
    this.onActionClick.emit();
  }

  toggleSearch() {
    this.isExpanded = true;
  }

  collapseSearch() {
    this.isExpanded = false;
  }

  onResetValue(): void {
    if (this.controlName) {
      this.formGroup.get(this.controlName)?.setValue('');
    }
  }

  /**
   * Trims leading and trailing spaces and replaces multiple spaces with a single space
   * @param value The string to trim
   * @returns The trimmed string
   */
  trimSpaces(value: string): string {
    if (!value) return '';
    // First trim leading and trailing spaces, then replace multiple spaces with a single space
    return value.trim().replace(/\s{2,}/g, ' ');
  }

  hasOnlyPathTraversalError(): boolean {
    const errors = this.formGroup.get(this.controlName!)?.errors;
    return !!errors && Object.keys(errors).length === 1 && 'path-traversal' in errors;
  }
}
